import React, { useState, useCallback } from 'react';
import { Tag, X, Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * PromoCodeInput - Promo code entry with validation
 * 
 * Features:
 * - Expandable/collapsible section
 * - Input field with apply button
 * - Success/error feedback
 * - Applied promo display with remove option
 * - Loading state during validation
 */
export default function PromoCodeInput({
  appliedCode = '',
  appliedDiscount = null, // { type, value, amount, message }
  onApply,
  onRemove,
  isValidating = false,
  className = '',
}) {
  const [isExpanded, setIsExpanded] = useState(!!appliedCode);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const hasAppliedCode = !!appliedCode && appliedDiscount;

  const handleApply = useCallback(async () => {
    if (!inputValue.trim()) {
      setError('Please enter a promo code');
      return;
    }

    setError('');
    const result = await onApply(inputValue.trim());
    
    if (result?.success) {
      setInputValue('');
      setError('');
    } else {
      setError(result?.message || 'Invalid promo code');
    }
  }, [inputValue, onApply]);

  const handleRemove = useCallback(() => {
    onRemove();
    setInputValue('');
    setError('');
  }, [onRemove]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  return (
    <div className={`bg-[#1a1f26] border border-white/10 rounded-2xl overflow-hidden ${className}`}>
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="
          w-full h-14 px-4
          flex items-center justify-between
          text-left
          hover:bg-white/5
          transition-colors
        "
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <div className={`
            w-10 h-10 rounded-lg flex items-center justify-center
            ${hasAppliedCode ? 'bg-[#10b981]/20' : 'bg-[#f5a623]/10'}
          `}>
            <Tag size={18} className={hasAppliedCode ? 'text-[#10b981]' : 'text-[#f5a623]'} />
          </div>
          <div>
            <span className="font-medium text-white">
              {hasAppliedCode ? 'Promo Applied' : 'Have a promo code?'}
            </span>
            {hasAppliedCode && (
              <p className="text-sm text-[#10b981]">
                {appliedCode} - {appliedDiscount.message}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-gray-400" />
        ) : (
          <ChevronDown size={20} className="text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 animate-slide-down">
          {hasAppliedCode ? (
            /* Applied Code Display */
            <div className="flex items-center justify-between p-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/20">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#10b981] flex items-center justify-center">
                  <Check size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">{appliedCode}</p>
                  <p className="text-sm text-[#10b981]">{appliedDiscount.message}</p>
                </div>
              </div>
              <button
                onClick={handleRemove}
                className="
                  w-10 h-10 rounded-lg
                  flex items-center justify-center
                  text-gray-400 hover:text-white hover:bg-white/10
                  transition-colors
                "
                aria-label="Remove promo code"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            /* Input Form */
            <>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value.toUpperCase());
                    setError('');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter code"
                  disabled={isValidating}
                  className="
                    flex-1 h-14 px-4
                    bg-[#252b33] border-2 border-white/10
                    rounded-xl
                    text-white text-base font-medium uppercase
                    placeholder-gray-500
                    focus:border-[#f5a623] focus:outline-none
                    disabled:opacity-50
                    transition-colors
                  "
                  aria-label="Promo code"
                />
                <button
                  onClick={handleApply}
                  disabled={isValidating || !inputValue.trim()}
                  className="
                    h-14 px-6 min-w-[100px]
                    bg-[#f5a623] text-[#0f1419]
                    font-semibold rounded-xl
                    disabled:opacity-50 disabled:cursor-not-allowed
                    hover:brightness-110
                    active:scale-[0.98]
                    transition-all
                  "
                >
                  {isValidating ? (
                    <Loader2 size={20} className="animate-spin mx-auto" />
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <p className="mt-2 text-sm text-[#ef4444]">{error}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Animation */}
      <style>{`
        @keyframes slide-down {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 200px; }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
